import BaseService from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';
import type { ChartDataPoint } from './ChartQueryService';

export type RailDashboardFilterOptions = {
  carTypes: string[];
  dealNames: string[];
  lesseeDomiciles: string[];
};

const FILTER_OPTIONS_PATH = 'rail-dashboard/filter-options';

function stringArrayFromBody(body: Record<string, unknown>, camelKey: string, pascalKey: string): string[] {
  const raw = body[camelKey] ?? body[pascalKey];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((x): x is string => typeof x === 'string');
}

export type RailTapeDashboardKpiLatest = {
  asOfDateIso: string;
  totalCollateralValue: number;
  weightedAvgLtvPct: number | null;
  totalRailcars: number;
  onLeaseUtilizationPct: number | null;
};

function parseNum(v: unknown): number | null {
  if (v == null) {
    return null;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseRailKpiRow(raw: unknown): RailTapeDashboardKpiLatest | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const asOf = o.asOfDateIso ?? o.AsOfDateIso;
  if (typeof asOf !== 'string' || asOf.length === 0) {
    return null;
  }
  const totalRailcars = parseNum(o.totalRailcars ?? o.TotalRailcars);
  const totalCollateralValue = parseNum(o.totalCollateralValue ?? o.TotalCollateralValue);
  if (totalRailcars == null || totalCollateralValue == null) {
    return null;
  }
  const weightedAvgLtvPct = parseNum(o.weightedAvgLtvPct ?? o.WeightedAvgLtvPct);
  const onLeaseUtilizationPct = parseNum(o.onLeaseUtilizationPct ?? o.OnLeaseUtilizationPct);

  return {
    asOfDateIso: asOf,
    totalRailcars: Math.trunc(totalRailcars),
    totalCollateralValue,
    weightedAvgLtvPct,
    onLeaseUtilizationPct,
  };
}

/**
 * Snapshot KPIs for SP_RAIL_LOAN_TAPE, optionally filtered by car type, deal, domicile, and as-of date.
 */

export type RailDashboardFilters = {
  carType: readonly string[];
  dealName: readonly string[];
  lesseeDomicile: readonly string[];
  /** Selected tape AS_OF_DATE (YYYY-MM-DD). Empty = latest. */
  asOfDate?: string;
};

function appendRailDashboardFilterParams(qs: URLSearchParams, filters: RailDashboardFilters) {
  for (const v of filters.carType) {
    const t = v.trim();
    if (t) qs.append('carType', t);
  }
  for (const v of filters.dealName) {
    const t = v.trim();
    if (t) qs.append('dealName', t);
  }
  for (const v of filters.lesseeDomicile) {
    const t = v.trim();
    if (t) qs.append('lesseeDomicile', t);
  }
  const asOf = filters.asOfDate?.trim();
  if (asOf) qs.set('asOfDate', asOf);
}

export type RailLeaseExpirationForecastRow = {
  forecastMonthIso: string;
  activeRailcarCount: number;
  endedRailcarCount: number;
  expiringInMonthCount: number;
};

function parseRailLeaseExpirationRows(raw: unknown): RailLeaseExpirationForecastRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: RailLeaseExpirationForecastRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const iso = r.forecastMonthIso ?? r.ForecastMonthIso;
    if (typeof iso !== 'string' || iso.length === 0) {
      continue;
    }
    const active = parseNum(r.activeRailcarCount ?? r.ActiveRailcarCount);
    const ended = parseNum(r.endedRailcarCount ?? r.EndedRailcarCount);
    const expiring = parseNum(r.expiringInMonthCount ?? r.ExpiringInMonthCount);
    if (active == null || ended == null || expiring == null) {
      continue;
    }
    out.push({
      forecastMonthIso: iso,
      activeRailcarCount: Math.trunc(active),
      endedRailcarCount: Math.trunc(ended),
      expiringInMonthCount: Math.trunc(expiring),
    });
  }
  return out;
}

/**
 * 12-month lease roll from the latest filtered tape (on-lease cars per KPI LEASE_STATUS, EXPIRATION_DATE),
 * same dashboard filters as tape charts / KPIs.
 */

export type RailLeaseStatusByMonthRow = {
  asOfMonth: string;
  leaseStatus: string;
  railcarCount: number;
};

function parseRailLeaseStatusByMonthRows(raw: unknown): RailLeaseStatusByMonthRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: RailLeaseStatusByMonthRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const month = r.asOfMonth ?? r.AsOfMonth;
    const status = r.leaseStatus ?? r.LeaseStatus;
    if (typeof month !== 'string' || month.length === 0) {
      continue;
    }
    if (typeof status !== 'string' || status.length === 0) {
      continue;
    }
    const count = parseNum(r.railcarCount ?? r.RailcarCount);
    if (count == null) {
      continue;
    }
    out.push({
      asOfMonth: month,
      leaseStatus: status,
      railcarCount: Math.trunc(count),
    });
  }
  return out;
}

/** Long-form points for {@link AutoChart} stacked-bar (xAxis = YYYY-MM, series = lease status). */
export function railLeaseStatusRowsToChartDataPoints(rows: readonly RailLeaseStatusByMonthRow[]): ChartDataPoint[] {
  return rows.map(r => ({
    xAxis: r.asOfMonth,
    series: r.leaseStatus,
    value: r.railcarCount,
  }));
}

/** Distinct railcars by month and On-Lease / Off-Lease (same filters as other rail dashboard charts). */

export class RailDashboardService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  getJsonBody = async (path: string) => {
    const raw = await this.get({ path: path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      return (await raw.json()) as Record<string, unknown>;
    }
    if (raw && typeof raw === 'object') {
      return raw as Record<string, unknown>;
    }
    return {};
  };

  fetchRailDashboardFilterOptions = async () => {
    const body = await this.getJsonBody(FILTER_OPTIONS_PATH);
    return {
      carTypes: stringArrayFromBody(body, 'carTypes', 'CarTypes'),
      dealNames: stringArrayFromBody(body, 'dealNames', 'DealNames'),
      lesseeDomiciles: stringArrayFromBody(body, 'lesseeDomiciles', 'LesseeDomiciles'),
    };
  };

  fetchRailDashboardAsOfDates = async () => {
    const body = await this.getJsonBody('rail-dashboard/as-of-dates');
    return stringArrayFromBody(body, 'asOfDates', 'AsOfDates');
  };

  fetchRailTapeDashboardKpi = async (filters: RailDashboardFilters) => {
    const qs = new URLSearchParams();
    appendRailDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `rail-dashboard/tape-kpi?${q}` : 'rail-dashboard/tape-kpi';

    const raw = await this.get({ path: path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return null;
      }
      const parsed: unknown = JSON.parse(text) as unknown;
      return parseRailKpiRow(parsed);
    }
    return parseRailKpiRow(raw);
  };

  fetchRailLeaseExpirationForecast = async (filters: RailDashboardFilters) => {
    const qs = new URLSearchParams();
    appendRailDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path =
      q.length > 0 ? `rail-dashboard/lease-expiration-forecast?${q}` : 'rail-dashboard/lease-expiration-forecast';

    const body = await this.getJsonBody(path);
    return parseRailLeaseExpirationRows(body);
  };

  fetchRailLeaseStatusByMonth = async (filters: RailDashboardFilters) => {
    const qs = new URLSearchParams();
    appendRailDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `rail-dashboard/lease-status-by-month?${q}` : 'rail-dashboard/lease-status-by-month';

    const body = await this.getJsonBody(path);
    return parseRailLeaseStatusByMonthRows(body);
  };
}
